import { Form, Input, Select } from 'antd'
import { useTranslation } from 'react-i18next'

import type { CarrierRoot } from '@repo/sdk'

import { useCarrierOptions } from './carrierConfig'
import { TrackingValidationPanel } from './TrackingValidationPanel'
import { useChangeTracking } from './useChangeTracking'
import { resolveValidationGate, useTrackingValidation } from './useTrackingValidation'
import { Popup, confirmPopup } from '@/components/popup'

interface ChangeTrackingModalProps {
  fulfillmentOrderId: string
  open: boolean
  onClose: () => void
  defaultCarrier?: CarrierRoot | null
  defaultTrackingNumber?: string | null
}

interface ChangeTrackingFormValues {
  carrier: CarrierRoot
  trackingNumber: string
}

export function ChangeTrackingModal({
  fulfillmentOrderId,
  open,
  onClose,
  defaultCarrier,
  defaultTrackingNumber,
}: ChangeTrackingModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<ChangeTrackingFormValues>()
  const carrierOptions = useCarrierOptions()
  const { changeTracking, isChanging } = useChangeTracking()
  const { validationResult, resetValidation, validate, isValidating, statusMessage } =
    useTrackingValidation()

  const submitChange = (values: ChangeTrackingFormValues) => {
    changeTracking({
      fulfillmentOrderId,
      carrier: values.carrier,
      trackingNumber: values.trackingNumber.trim(),
      onDone: () => {
        form.resetFields()
        resetValidation()
        onClose()
      },
    })
  }

  /** Validation did not pass cleanly: show the reason and ask whether to save anyway. */
  const confirmChangeAnyway = (values: ChangeTrackingFormValues, warning: string) => {
    confirmPopup({
      title: t('pages.orders.changeTracking.confirmTitle'),
      content: (
        <>
          <p>{warning}</p>
          <p>{t('pages.orders.trackingValidation.proceedAnywayChange')}</p>
        </>
      ),
      okText: t('pages.orders.trackingValidation.proceedAnywayButton'),
      okButtonProps: { 'data-testid': 'change-tracking-confirm-ok-btn' },
      cancelButtonProps: { 'data-testid': 'change-tracking-confirm-cancel-btn' },
      onOk: () => submitChange(values),
    })
  }

  const handleOk = () => {
    void form.validateFields().then(async (values) => {
      const outcome = await validate(values.carrier, values.trackingNumber)
      const gate = resolveValidationGate(
        outcome,
        statusMessage,
        t('pages.orders.trackingValidation.requestFailedProceed'),
      )
      if (gate.action === 'block') return
      if (gate.action === 'confirm') {
        confirmChangeAnyway(values, gate.warning)
        return
      }
      submitChange(values)
    })
  }

  const handleCancel = () => {
    form.resetFields()
    resetValidation()
    onClose()
  }

  const handleValidateOnly = () => {
    void form.validateFields(['carrier', 'trackingNumber']).then(async (values) => {
      await validate(values.carrier, values.trackingNumber)
    })
  }

  return (
    <Popup
      data-testid="change-tracking-modal"
      title={t('pages.orders.changeTracking.title')}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={isChanging || isValidating}
      okText={t('common.buttons.save')}
      cancelText={t('common.buttons.cancel')}
      okButtonProps={{ 'data-testid': 'change-tracking-modal-ok-btn' }}
      cancelButtonProps={{ 'data-testid': 'change-tracking-modal-cancel-btn' }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          carrier: defaultCarrier ?? undefined,
          trackingNumber: defaultTrackingNumber ?? '',
        }}
        onValuesChange={(changed) => {
          if ('carrier' in changed || 'trackingNumber' in changed) resetValidation()
        }}
      >
        <Form.Item
          name="carrier"
          label={t('pages.orders.fields.courierCompany')}
          rules={[{ required: true, message: t('pages.orders.changeTracking.carrierRequired') }]}
        >
          <Select
            showSearch
            options={carrierOptions}
            optionFilterProp="label"
            data-testid="change-tracking-carrier-select"
          />
        </Form.Item>
        <Form.Item
          name="trackingNumber"
          label={t('pages.orders.fields.trackingNumber')}
          rules={[{ required: true, message: t('pages.orders.changeTracking.trackingRequired') }]}
        >
          <Input maxLength={100} data-testid="change-tracking-tracking-input" />
        </Form.Item>
        <TrackingValidationPanel
          result={validationResult}
          isValidating={isValidating}
          onValidate={handleValidateOnly}
          testIdPrefix="change-tracking"
        />
      </Form>
    </Popup>
  )
}
